import type {
  TableCallParticipantCallData,
  TableCallParticipantUpdatePayload,
} from "@bezoned/desktop-bridge/table-call-messaging.js";
import { computeCallIndicatorColor } from "@bezoned/shared/utils/call-indicator-color.js";
import type { CallIndicatorColor } from "@bezoned/shared/utils/call-indicator-color.js";
import { computeEffectiveSpeaking } from "@bezoned/shared/utils/compute-effective-speaking.js";
import { Tree } from "fluid-framework";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { useAuth } from "../auth/use-auth.js";
import { isUserInAreaWithoutVoiceCall } from "../fluid/fluid.service.js";
import { useFluid } from "../fluid/hooks/use-fluid.js";
import { speakingStore } from "../table-call/speaking-state.js";
import type {
  RemoteCallPhase,
  RemoteCallState,
} from "../table-call/table-call-participant-directory.js";
import type {
  TableCallAudioReadiness,
  TableCallStatus,
} from "../table-call/table-call.context.js";
import { useTableCall } from "../table-call/use-table-call.js";
import { createTableCallDeltaSenders } from "./table-call-sync/create-table-call-delta-senders.js";
import { createInboundRequestHandler } from "./table-call-sync/inbound-request-handler.js";
import { buildRecoveryStatePayload } from "./table-call-sync/recovery-state-sender.js";
import { useDesktopBridgeRoom } from "./use-desktop-bridge-room.js";
import { useDesktopBridge } from "./use-desktop-bridge.js";

const RECOVERY_RESYNC_INTERVAL_MS = 30_000;
const RECOVERY_STATUS_TRIGGERS = new Set<TableCallStatus>([
  "connected",
  "joining",
  "leaving",
  "disconnected",
]);

interface CallParticipantRuntimeState {
  isMuted: boolean;
  connected: boolean;
  indicatorColor: CallIndicatorColor;
}

interface SyncedSpeakingState {
  isSpeaking: boolean;
  volumeLevel: number;
}

export const DesktopBridgeCallSyncListener = () => {
  const { user } = useAuth();
  const { sharedTree } = useFluid();
  const {
    status,
    audioReadiness,
    meeting,
    participantDirectory,
    microphoneActive,
    canControlMicrophone,
    setMicrophoneAlwaysOn,
    startPushToTalk,
    stopPushToTalk,
    sessionPhase,
  } = useTableCall();

  const {
    status: bridgeConnectionStatus,
    instanceId,
    sendJson,
    onJsonMessage,
  } = useDesktopBridge();

  const bridgeRoom = useDesktopBridgeRoom();
  const previousStatusRef = useRef<TableCallStatus | null>(null);
  const previousAudioReadinessRef = useRef<TableCallAudioReadiness | null>(
    null
  );
  const previousBridgeStatusRef = useRef<string | null>(null);
  const runtimeStateByUserIdRef = useRef(
    new Map<string, CallParticipantRuntimeState>()
  );
  const lastSentSpeakingStatesRef = useRef(
    new Map<string, SyncedSpeakingState>()
  );
  const speakingSubscriptionsRef = useRef(new Map<string, () => void>());
  const nextProjectionSequence = useMemo(() => {
    let lastProjectionSequence = 0;
    return () => {
      lastProjectionSequence = Math.max(Date.now(), lastProjectionSequence + 1);
      return lastProjectionSequence;
    };
  }, []);
  const volatileRef = useRef({
    audioReadiness,
    canControlMicrophone,
    meeting,
    microphoneActive,
    participantDirectory,
    sessionPhase,
    sharedTree,
    status,
  });
  volatileRef.current = {
    audioReadiness,
    canControlMicrophone,
    meeting,
    microphoneActive,
    participantDirectory,
    sessionPhase,
    sharedTree,
    status,
  };

  const {
    sendTableCallState,
    sendSpeakingChange,
    sendSelfUpdate,
    sendParticipantUpdate,
    sendTableCallDisconnected,
  } = useMemo(
    () =>
      createTableCallDeltaSenders({
        bridgeRoom,
        instanceId,
        nextProjectionSequence,
        sendJson,
      }),
    [instanceId, bridgeRoom, nextProjectionSequence, sendJson]
  );

  const buildCallParticipantData = useCallback(
    (
      userId: string,
      connected: boolean,
      isMuted: boolean,
      isSelf: boolean,
      remotePhase?: RemoteCallPhase
    ): TableCallParticipantCallData => {
      const v = volatileRef.current;
      const rawSpeakingState = speakingStore.getState(userId);
      const effectiveSpeakingState = computeEffectiveSpeaking({
        connected,
        isMuted,
        // The bridge only decides whether the state is visible on the local
        // desktop overlay; the shared selector owns the speaking rule itself.
        isVisibleInContext: true,
        raw: rawSpeakingState,
      });
      const isInAreaWithoutVoiceCall = isUserInAreaWithoutVoiceCall({
        sharedTree: v.sharedTree,
        userId,
      });
      const indicatorColor = computeCallIndicatorColor({
        audioTrackUnhealthy: false,
        callStatus: v.status,
        connected,
        hasMeeting: Boolean(v.meeting),
        isInAreaWithoutVoiceCall,
        isMuted,
        isSelf,
      });

      return {
        connected,
        indicatorColor,
        isMuted,
        isSpeaking: effectiveSpeakingState.isSpeaking,
        remotePhase,
        userId,
        volumeLevel: effectiveSpeakingState.volumeLevel,
      };
    },
    []
  );

  const buildCallRecoveryState = useCallback(() => {
    const v = volatileRef.current;
    const participants: TableCallParticipantCallData[] = [];

    const remotesByParticipantId = new Map<string, RemoteCallState>();
    for (const remote of v.participantDirectory?.getParticipantRemotes() ??
      []) {
      remotesByParticipantId.set(remote.participantId, remote);
    }

    for (const [userId, remote] of remotesByParticipantId) {
      if (userId === user.id) {
        continue;
      }
      participants.push(
        buildCallParticipantData(
          userId,
          remote.phase === "ready",
          !remote.audioEnabled,
          false,
          remote.phase
        )
      );
    }

    const selfConnected = v.status === "connected";
    participants.push(
      buildCallParticipantData(
        user.id,
        selfConnected,
        selfConnected ? !v.microphoneActive : true,
        true
      )
    );

    return buildRecoveryStatePayload({
      audioReadiness: v.audioReadiness,
      canControlMicrophone: v.canControlMicrophone,
      microphoneActive: v.microphoneActive,
      participants,
      selfName: user.name,
      selfUserId: user.id,
      sessionPhase: v.sessionPhase,
      status: v.status,
    });
  }, [buildCallParticipantData, user.id, user.name]);

  const sendRecoveryState = useCallback(
    (_reason: string) => {
      const payload = buildCallRecoveryState();
      runtimeStateByUserIdRef.current.clear();
      lastSentSpeakingStatesRef.current = new Map(
        payload.participants.map((participant) => [
          participant.userId,
          {
            isSpeaking: participant.isSpeaking ?? false,
            volumeLevel:
              (participant.isSpeaking ?? false)
                ? (participant.volumeLevel ?? 0)
                : 0,
          },
        ])
      );

      for (const participant of payload.participants) {
        runtimeStateByUserIdRef.current.set(participant.userId, {
          connected: participant.connected,
          indicatorColor: participant.indicatorColor ?? "unmuted",
          isMuted: participant.isMuted,
        });
      }

      sendTableCallState(payload);
    },
    [buildCallRecoveryState, sendTableCallState]
  );

  const sendSpeakingState = useCallback(
    (userId: string, speakingState: SyncedSpeakingState) => {
      const runtimeState = runtimeStateByUserIdRef.current.get(userId);
      const normalizedSpeakingState = runtimeState
        ? computeEffectiveSpeaking({
            connected: runtimeState.connected,
            isMuted: runtimeState.isMuted,
            // The bridge only decides whether the state is visible on the local
            // desktop overlay; the shared selector owns the speaking rule itself.
            isVisibleInContext: true,
            raw: speakingState,
          })
        : speakingState;
      const previousSpeakingState =
        lastSentSpeakingStatesRef.current.get(userId) ?? null;

      if (
        previousSpeakingState?.isSpeaking ===
          normalizedSpeakingState.isSpeaking &&
        previousSpeakingState?.volumeLevel ===
          normalizedSpeakingState.volumeLevel
      ) {
        return;
      }

      lastSentSpeakingStatesRef.current.set(userId, normalizedSpeakingState);
      sendSpeakingChange({
        isSpeaking: normalizedSpeakingState.isSpeaking,
        userId,
        volumeLevel: normalizedSpeakingState.volumeLevel,
      });
    },
    [sendSpeakingChange]
  );

  const sendSpeakingStateRef = useRef(sendSpeakingState);
  sendSpeakingStateRef.current = sendSpeakingState;

  const emitParticipantRuntimeUpdate = useCallback(
    ({
      userId,
      connected,
      isMuted,
      isSelf,
      remotePhase,
      force = false,
    }: {
      userId: string;
      connected: boolean;
      isMuted: boolean;
      isSelf: boolean;
      remotePhase?: RemoteCallPhase;
      force?: boolean;
    }) => {
      const callData = buildCallParticipantData(
        userId,
        connected,
        isMuted,
        isSelf
      );
      const nextState: CallParticipantRuntimeState = {
        connected: callData.connected,
        indicatorColor: callData.indicatorColor ?? "unmuted",
        isMuted: callData.isMuted,
      };
      const previousState = runtimeStateByUserIdRef.current.get(userId);

      const payload: Omit<
        TableCallParticipantUpdatePayload,
        "projectionSequence"
      > = { userId };
      if (force || previousState?.isMuted !== nextState.isMuted) {
        payload.isMuted = nextState.isMuted;
      }
      if (force || previousState?.connected !== nextState.connected) {
        payload.connected = nextState.connected;
      }
      if (force || previousState?.indicatorColor !== nextState.indicatorColor) {
        payload.indicatorColor = nextState.indicatorColor;
      }
      if (remotePhase) {
        payload.remotePhase = remotePhase;
      }

      if (Object.keys(payload).length > 1) {
        sendParticipantUpdate(payload);
      }

      runtimeStateByUserIdRef.current.set(userId, nextState);
      sendSpeakingStateRef.current(userId, speakingStore.getState(userId));
    },
    [buildCallParticipantData, sendParticipantUpdate]
  );

  const emitParticipantRuntimeUpdateRef = useRef(emitParticipantRuntimeUpdate);
  emitParticipantRuntimeUpdateRef.current = emitParticipantRuntimeUpdate;

  const sendRecoveryStateRef = useRef(sendRecoveryState);
  sendRecoveryStateRef.current = sendRecoveryState;

  const handlePttPressed = useCallback(() => {
    const v = volatileRef.current;
    if (v.status === "disconnected" || !v.canControlMicrophone) {
      return;
    }
    startPushToTalk().catch(recordErrorInSentry);
  }, [startPushToTalk]);

  const handlePttReleased = useCallback(() => {
    stopPushToTalk().catch(recordErrorInSentry);
  }, [stopPushToTalk]);

  const handleMicrophoneToggleRequest = useCallback(
    async ({ desiredActive }: { desiredActive: boolean }) => {
      const v = volatileRef.current;
      if (!v.canControlMicrophone) {
        return;
      }
      try {
        await setMicrophoneAlwaysOn(desiredActive);
      } catch (error) {
        recordErrorInSentry(error);
      }
    },
    [setMicrophoneAlwaysOn]
  );

  const onBridgeJsonMessage = useMemo(
    () =>
      createInboundRequestHandler({
        bridgeRoom,
        onMicrophoneToggleRequest: (toggleRequestPayload) => {
          handleMicrophoneToggleRequest(toggleRequestPayload).catch(
            recordErrorInSentry
          );
        },
        onPttPressed: handlePttPressed,
        onPttReleased: handlePttReleased,
        onStateRequest: () => {
          sendRecoveryStateRef.current("state-request");
        },
      }),
    [
      bridgeRoom,
      handleMicrophoneToggleRequest,
      handlePttPressed,
      handlePttReleased,
    ]
  );

  useEffect(
    () => onJsonMessage(onBridgeJsonMessage),
    [onBridgeJsonMessage, onJsonMessage]
  );

  useEffect(() => {
    sendSelfUpdate({
      audioReadiness,
      canControlMicrophone,
      microphoneActive,
      sessionPhase,
    });

    const connected = status === "connected";
    emitParticipantRuntimeUpdate({
      connected,
      isMuted: connected ? !microphoneActive : true,
      isSelf: true,
      userId: user.id,
    });
  }, [
    microphoneActive,
    canControlMicrophone,
    audioReadiness,
    status,
    sessionPhase,
    user.id,
    sendSelfUpdate,
    emitParticipantRuntimeUpdate,
  ]);

  useEffect(() => {
    if (bridgeConnectionStatus !== "ready") {
      previousBridgeStatusRef.current = bridgeConnectionStatus;
      stopPushToTalk().catch(recordErrorInSentry);
      return;
    }

    if (previousBridgeStatusRef.current !== "ready") {
      sendRecoveryState("bridge-ready");
    }
    previousBridgeStatusRef.current = bridgeConnectionStatus;
  }, [bridgeConnectionStatus, sendRecoveryState, stopPushToTalk]);

  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    const previousAudioReadiness = previousAudioReadinessRef.current;
    const hasChanged = previousStatus !== status;
    const audioReadinessChanged = previousAudioReadiness !== audioReadiness;
    if (hasChanged && status === "disconnected") {
      stopPushToTalk().catch(recordErrorInSentry);
      lastSentSpeakingStatesRef.current = new Map();
    }

    if (
      (hasChanged && RECOVERY_STATUS_TRIGGERS.has(status)) ||
      audioReadinessChanged
    ) {
      sendRecoveryState(`status-${status}:audio-${audioReadiness}`);
    }

    if (hasChanged && status === "disconnected") {
      sendTableCallDisconnected("table-call-disconnected");
    }

    previousStatusRef.current = status;
    previousAudioReadinessRef.current = audioReadiness;
  }, [
    audioReadiness,
    sendRecoveryState,
    sendTableCallDisconnected,
    status,
    stopPushToTalk,
  ]);

  useEffect(
    () => () => {
      stopPushToTalk().catch(recordErrorInSentry);
    },
    [stopPushToTalk]
  );

  useEffect(() => {
    if (bridgeConnectionStatus !== "ready" || status !== "connected") {
      return;
    }

    const intervalId = setInterval(() => {
      sendRecoveryStateRef.current("periodic-resync");
    }, RECOVERY_RESYNC_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [bridgeConnectionStatus, status]);

  useEffect(() => {
    const speakingListenerMap = speakingSubscriptionsRef.current;

    const removeSpeakingListener = (userId: string) => {
      const existing = speakingListenerMap.get(userId);
      if (!existing) {
        return;
      }
      existing();
      speakingListenerMap.delete(userId);
    };

    const upsertSpeakingListener = (userId: string) => {
      if (speakingListenerMap.has(userId)) {
        return;
      }

      const unsubscribe = speakingStore.subscribe(userId, () => {
        sendSpeakingStateRef.current(userId, speakingStore.getState(userId));
      });
      speakingListenerMap.set(userId, unsubscribe);
    };

    const syncParticipants = () => {
      const remotesByParticipantId = new Map<string, RemoteCallState>();
      for (const remote of participantDirectory?.getParticipantRemotes() ??
        []) {
        remotesByParticipantId.set(remote.participantId, remote);
      }

      const currentUserIds = new Set(remotesByParticipantId.keys());
      let removedAny = false;
      for (const userId of [...speakingListenerMap.keys()]) {
        if (userId === user.id || currentUserIds.has(userId)) {
          continue;
        }
        removeSpeakingListener(userId);
        runtimeStateByUserIdRef.current.delete(userId);
        lastSentSpeakingStatesRef.current.delete(userId);
        removedAny = true;
      }

      for (const remote of remotesByParticipantId.values()) {
        emitParticipantRuntimeUpdateRef.current({
          connected: remote.phase === "ready",
          force: true,
          isMuted: !remote.audioEnabled,
          isSelf: false,
          remotePhase: remote.phase,
          userId: remote.participantId,
        });
        upsertSpeakingListener(remote.participantId);
      }

      if (removedAny) {
        sendRecoveryStateRef.current("participant-left");
      }
    };

    const unsubscribe = participantDirectory?.subscribeAll(syncParticipants);
    syncParticipants();

    // The remote directory does not include `meeting.self`, so the
    // per-participant reconciliation above never subscribes to it. Without this
    // explicit subscription the local user's own
    // speaking volume is never streamed to the desktop overlay, so the self
    // avatar's glowing aura would stay frozen between recovery snapshots.
    upsertSpeakingListener(user.id);

    return () => {
      unsubscribe?.();
      for (const userId of [...speakingListenerMap.keys()]) {
        removeSpeakingListener(userId);
      }
    };
  }, [participantDirectory, user.id]);

  // Re-evaluate indicator colors when users move between positions
  // (entrance area / rooms) so the desktop overlay updates immediately
  // instead of waiting for the next periodic resync.
  useEffect(() => {
    if (bridgeConnectionStatus !== "ready" || status === "disconnected") {
      return;
    }

    const { entranceArea } = sharedTree.root;
    const { rooms } = sharedTree.root;

    const handlePositionChange = () => {
      sendRecoveryStateRef.current("position-change");
    };

    const unsubscribeEntrance = Tree.on(
      entranceArea,
      "treeChanged",
      handlePositionChange
    );

    const roomUnsubscribers = rooms.map((room) =>
      Tree.on(room, "treeChanged", handlePositionChange)
    );

    return () => {
      unsubscribeEntrance();
      for (const unsubscribe of roomUnsubscribers) {
        unsubscribe();
      }
    };
  }, [bridgeConnectionStatus, sharedTree, status]);

  return null;
};
